import { Combobox, Loader, TextInput, useCombobox } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraFieldData2, JiraSprintLiteData } from '../../../../api/endpoints/jira';
import { Icon } from '../../../../concepts/resources/Icon';
import { useResolve } from '../../../Container';

interface SprintInputOptions {
    autoFocus?: boolean;
    field: Pick<JiraFieldData2, 'name' | 'required' | 'hasDefaultValue'> & { project?: { id: string } };
    value: JiraSprintLiteData | null;
    error?: boolean;
    onChange: (value: JiraSprintLiteData | null) => void;
    onSubmit: (value: JiraSprintLiteData | null) => void;
}

const useJiraSprintQuery = (jiraProjectId: string, search: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['jiraSprintMenu', jiraProjectId, search],
        queryFn: async () => {
            if (!!search) {
                // Yeah really
                const results = (await api.exec(v2.jira.fields.suggest('cf[10020]', search)))
                    .map(x => ({ id: x.value, name: x.label } as JiraSprintLiteData));
                return results;
            }
            else if (!!jiraProjectId) {
                const results = await api.exec(v2.jira.projects.sprints.list(jiraProjectId));
                return results.map(x => ({ id: x.id, name: x.name } as JiraSprintLiteData));
            }
            else {
                return [{
                    id: 'null',
                    name: 'Start typing to search for a sprint...'
                }];
            }
        },
    });
};

export const JiraSprintInput = React.memo(({ field, value, error, onChange, onSubmit, autoFocus = false }: SprintInputOptions) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState('');
    const { data, isLoading } = useJiraSprintQuery(field.project?.id, search);
    const sprints = data || [];
    const empty = search.length > 0 && sprints.length === 0 && !isLoading;

    // Find sprint by ID
    const findSprintById = (id: string): JiraSprintLiteData | null => {
        if (!id || !sprints.length) return null;
        return sprints.find(x => x.id === id) || null;
    };

    // Clear button to reset the selected value
    const ClearButton = value ? (
        <div
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setSearch('');
            }}
        >
            <Icon size="1" src="deqo://icons/hero/x" />
        </div>
    ) : null;

    return (
        <Combobox
            onOptionSubmit={(optionValue) => {
                const sprint = findSprintById(optionValue);
                onChange(sprint);
                onSubmit(sprint);
                combobox.closeDropdown();
            }}
            withinPortal={false}
            store={combobox}
        >
            <Combobox.Target>
                <TextInput
                    label={field.name}
                    placeholder={`Search for ${field.name.toLowerCase()}`}
                    required={field.required && !field.hasDefaultValue}
                    value={value ? value.name : search}
                    error={error}
                    autoFocus={autoFocus}
                    onChange={(event) => {
                        const query = event.currentTarget.value;
                        if (value) {
                            // Clear the selected value if user starts typing
                            onChange(null);
                        }
                        setSearch(query);
                        combobox.resetSelectedOption();
                        combobox.openDropdown();
                    }}
                    onClick={() => {
                        combobox.openDropdown();
                        if (search.length > 0 && !data) {
                            setSearch(search);
                        }
                    }}
                    onFocus={() => {
                        combobox.openDropdown();
                        if (search.length > 0 && !data) {
                            setSearch(search);
                        }
                    }}
                    onBlur={() => combobox.closeDropdown()}
                    rightSection={isLoading ? <Loader size={18} /> : ClearButton}
                />
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {sprints.map((sprint) => (
                        <Combobox.Option value={sprint.id} key={sprint.id} disabled={sprint.id === 'null'}>
                            {sprint.name}
                        </Combobox.Option>
                    ))}
                    {empty && <Combobox.Empty>No sprints found</Combobox.Empty>}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
});
